import React, { useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import LoadingScreen from '@/components/ui/LoadingScreen';

/**
 * Determine where the user should be routed based on auth and progress flags.
 *
 * This runs for authenticated users via the catch-all route in AppRoutes.
 * After PR #30 flipped the onboarding gate (OnboardingFlow is no longer a
 * gate; it runs on demand from WeekHome's "Start Here" CTA), every
 * authenticated user past consent should land on /home. The only case
 * where we override that is Path B users who have not yet seen their
 * one-time PathBOrientation screen.
 *
 * Unauthenticated users return null so the vanilla JS app can render the
 * login shell.
 */
function getRoute(user, progress) {
  if (!user) return null;

  // Path B users see the orientation screen once before /home.
  if (progress?.path === 'B' && !progress?.path_b_orientation_complete) {
    return '/path-b-orientation';
  }

  // Everyone else (Path A, or Path B past orientation) goes to /home.
  // This is what "always show the full app after consent" means — no
  // more null-return stranding users on a blank screen when they land
  // on / or any unrecognised URL.
  return '/home';
}

export default function OnboardingGate() {
  const { user, userProgress, loading } = useApp();

  const route = useMemo(
    () => getRoute(user, userProgress),
    [user, userProgress]
  );

  if (loading) return <LoadingScreen />;

  // No route: let the vanilla JS app handle the UI
  if (!route) return null;

  return <Navigate to={route} replace />;
}
